/**
 * LoyaltyLevelsEditor Component
 * Editor for loyalty level configuration with:
 * - 5 fixed tiers (Bronze → Diamond) - cannot add/remove
 * - Locked tier names - cannot edit
 * - Global earn rate (spend X baht = get Y points)
 * - Per-tier: minPoints, discountPercent
 * - Validation: no negatives, minPoints ascending
 */

import { ChangeEvent, useMemo } from 'react';
import { Star, Trophy, AlertTriangle, Lightbulb } from 'lucide-react';
import type { LoyaltyLevel } from '../types';

// Fixed tier names (locked, cannot be edited)
const FIXED_TIERS = [
    { level: 1, name: 'bronze', nameTh: 'ทองแดง' },
    { level: 2, name: 'silver', nameTh: 'เงิน' },
    { level: 3, name: 'gold', nameTh: 'ทอง' },
    { level: 4, name: 'platinum', nameTh: 'แพลตินัม' },
    { level: 5, name: 'diamond', nameTh: 'เพชร' },
];

interface LoyaltyLevelsEditorProps {
    levels: LoyaltyLevel[];
    earnPointsPerSpend?: number;
    earnPointsAmount?: number;
    onChange: (levels: LoyaltyLevel[]) => void;
    onEarnRateChange?: (earnPointsPerSpend: number, earnPointsAmount: number) => void;
    disabled?: boolean;
}

export function LoyaltyLevelsEditor({
    levels,
    earnPointsPerSpend = 100,
    earnPointsAmount = 1,
    onChange,
    onEarnRateChange,
    disabled = false
}: LoyaltyLevelsEditorProps) {

    // Ensure we always have 5 levels with correct names
    const normalizedLevels = useMemo(() => {
        return FIXED_TIERS.map((tier) => {
            const existing = levels.find(l => l.level === tier.level);
            return {
                level: tier.level,
                name: tier.name,
                nameTh: tier.nameTh,
                minPoints: existing?.minPoints ?? 0,
                discountPercent: existing?.discountPercent ?? 0,
            };
        });
    }, [levels]);

    // Validation: check if minPoints are in ascending order
    const validationErrors = useMemo(() => {
        const errors: Record<number, string> = {};
        for (let i = 1; i < normalizedLevels.length; i++) {
            if (normalizedLevels[i].minPoints <= normalizedLevels[i - 1].minPoints) {
                errors[i] = `ต้องมากกว่า ${normalizedLevels[i - 1].minPoints.toLocaleString()}`;
            }
        }
        return errors;
    }, [normalizedLevels]);

    const hasErrors = Object.keys(validationErrors).length > 0;

    const handleLevelChange = (index: number, field: 'minPoints' | 'discountPercent', value: number) => {
        // Ensure non-negative values
        const safeValue = Math.max(0, value);
        // For discountPercent, cap at 100
        const finalValue = field === 'discountPercent' ? Math.min(100, safeValue) : safeValue;

        const newLevels = normalizedLevels.map((level, i) =>
            i === index ? { ...level, [field]: finalValue } : level
        );
        onChange(newLevels);
    };

    const handleEarnRateChange = (field: 'earnPointsPerSpend' | 'earnPointsAmount', value: number) => {
        if (!onEarnRateChange) return;
        // earnPointsPerSpend must be at least 1 (cannot divide by 0)
        if (field === 'earnPointsPerSpend') {
            onEarnRateChange(Math.max(1, value), earnPointsAmount);
        } else {
            onEarnRateChange(earnPointsPerSpend, Math.max(0, value));
        }
    };

    return (
        <div className="space-y-6 md:col-span-2">
            {/* Global Earn Rate Section */}
            <div className="bg-gradient-to-r from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-4">
                <h3 className="text-sm font-bold text-yellow-800 dark:text-yellow-300 mb-3 flex items-center gap-2">
                    <Star className="h-4 w-4" />
                    การได้รับคะแนน (ใช้กับทุกระดับ)
                </h3>
                <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="text-gray-700 dark:text-gray-300">ทุกๆ</span>
                    <input
                        type="number"
                        value={earnPointsPerSpend}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            handleEarnRateChange('earnPointsPerSpend', Number(e.target.value))
                        }
                        disabled={disabled}
                        min={1}
                        className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-center font-bold text-gray-900 dark:text-white focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 disabled:opacity-50"
                    />
                    <span className="text-gray-700 dark:text-gray-300">บาท ได้รับ</span>
                    <input
                        type="number"
                        value={earnPointsAmount}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            handleEarnRateChange('earnPointsAmount', Number(e.target.value))
                        }
                        disabled={disabled}
                        min={0}
                        step={0.1}
                        className="w-20 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-center font-bold text-gray-900 dark:text-white focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 disabled:opacity-50"
                    />
                    <span className="text-gray-700 dark:text-gray-300">คะแนน</span>
                </div>
            </div>

            {/* Tier Configuration Table */}
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
                <div className="bg-gray-50 dark:bg-gray-900 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-sm font-bold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                        <Trophy className="h-4 w-4 text-yellow-500" />
                        ระดับสมาชิก (5 ระดับ)
                    </h3>
                </div>

                {/* Table Header */}
                <div className="hidden md:grid md:grid-cols-4 gap-4 px-4 py-3 bg-gray-100 dark:bg-gray-900/50 text-xs font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                    <div>ระดับ</div>
                    <div>คะแนนขั้นต่ำ</div>
                    <div>ส่วนลด (%)</div>
                    <div></div>
                </div>

                {/* Table Body */}
                <div className="divide-y divide-gray-100 dark:divide-gray-700">
                    {normalizedLevels.map((level, index) => (
                        <div
                            key={level.level}
                            className="grid grid-cols-1 md:grid-cols-4 gap-4 px-4 py-4 items-center"
                        >
                            {/* Level Name (Locked) */}
                            <div className="flex items-center gap-3">
                                <div className={`
                                    w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white
                                    ${level.name === 'bronze' ? 'bg-amber-600' : ''}
                                    ${level.name === 'silver' ? 'bg-gray-400' : ''}
                                    ${level.name === 'gold' ? 'bg-yellow-500' : ''}
                                    ${level.name === 'platinum' ? 'bg-slate-500' : ''}
                                    ${level.name === 'diamond' ? 'bg-cyan-500' : ''}
                                `}>
                                    {level.level}
                                </div>
                                <div>
                                    <div className="font-bold text-gray-900 dark:text-white capitalize">
                                        {level.name}
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                        {level.nameTh}
                                    </div>
                                </div>
                            </div>

                            {/* Min Points */}
                            <div>
                                <label className="md:hidden text-xs text-gray-500 mb-1 block">คะแนนขั้นต่ำ</label>
                                <div className="relative">
                                    <input
                                        type="number"
                                        value={level.minPoints}
                                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                            handleLevelChange(index, 'minPoints', Number(e.target.value))
                                        }
                                        disabled={disabled || index === 0} // Level 1 always starts at 0
                                        min={0}
                                        className={`
                                            w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                                            focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500
                                            disabled:opacity-50 disabled:bg-gray-100 dark:disabled:bg-gray-800
                                            ${validationErrors[index] ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}
                                        `}
                                    />
                                    {validationErrors[index] && (
                                        <p className="absolute text-xs text-red-500 mt-1">
                                            {validationErrors[index]}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Discount Percent */}
                            <div>
                                <label className="md:hidden text-xs text-gray-500 mb-1 block">ส่วนลด (%)</label>
                                <div className="relative">
                                    <input
                                        type="number"
                                        value={level.discountPercent}
                                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                            handleLevelChange(index, 'discountPercent', Number(e.target.value))
                                        }
                                        disabled={disabled}
                                        min={0}
                                        max={100}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 disabled:opacity-50"
                                    />
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">%</span>
                                </div>
                            </div>

                            {/* Status */}
                            <div className="hidden md:flex items-center justify-end">
                                {level.discountPercent > 0 && (
                                    <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-medium rounded-full">
                                        ลด {level.discountPercent}%
                                    </span>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Validation Warning */}
                {hasErrors && (
                    <div className="px-4 py-3 bg-red-50 dark:bg-red-900/20 border-t border-red-200 dark:border-red-800">
                        <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-2">
                            <AlertTriangle className="h-4 w-4" />
                            คะแนนขั้นต่ำของแต่ละระดับต้องมากกว่าระดับก่อนหน้า
                        </p>
                    </div>
                )}
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
                <p className="text-sm text-blue-700 dark:text-blue-300 flex items-center gap-2">
                    <Lightbulb className="h-4 w-4" />
                    <strong>สิทธิประโยชน์:</strong> ลูกค้าจะได้รับส่วนลดท้ายบิลตาม % ที่ตั้งไว้ในแต่ละระดับ
                </p>
            </div>
        </div>
    );
}
